/**
 * Twilio Conversations API service.
 * - getToken(identity) — get Conversations access token
 * - getOrCreateConversation({ caseId, accessionId, patientWhatsAppNumber }) — create or get conversation
 * - getConversation({ caseId, accessionId }) — get conversationSid by case
 */
import { twilioConfig } from "./authConfig";

const TWILIO_API_BASE = twilioConfig.apiBase;
const endpoints = twilioConfig.endpoints;

/**
 * Retry helper for network errors (ERR_CONNECTION_REFUSED, etc.)
 * Retries up to 3 times with exponential backoff delays.
 */
async function fetchWithRetry(url, options = {}, maxRetries = 3) {
  let lastError;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetch(url, options);
      return response;
    } catch (err) {
      lastError = err;
      
      // Don't retry if request was aborted
      if (err.name === 'AbortError' || options.signal?.aborted) {
        throw err;
      }
      
      const isNetworkError =
        err?.message?.includes("Failed to fetch") ||
        err?.message?.includes("ERR_CONNECTION_REFUSED") ||
        err?.message?.includes("NetworkError") ||
        err?.name === "TypeError";
      if (!isNetworkError || attempt === maxRetries) {
        throw err;
      }
      // Wait before retry (exponential backoff: 1.5s * (attempt + 1))
      await new Promise((resolve) => setTimeout(resolve, 1500 * (attempt + 1)));
    }
  }
  throw lastError || new Error("Network request failed after retries");
}

export async function getTwilioToken(identity = "radiologist") {
  const url = `${TWILIO_API_BASE}${endpoints.token}`;
  const response = await fetchWithRetry(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ identity: String(identity).replace(/[^a-zA-Z0-9_-]/g, "_") }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Token failed: ${response.status}`);
  }
  const data = await response.json();
  return data.token;
}

export async function getOrCreateConversation({ caseId, accessionId, patientWhatsAppNumber } = {}) {
  const url = `${TWILIO_API_BASE}${endpoints.conversation}`;
  const response = await fetchWithRetry(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      caseId: caseId || undefined,
      accessionId: accessionId || undefined,
      patientWhatsAppNumber: patientWhatsAppNumber || undefined,
    }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    const errorMsg = err.error || `Conversation failed: ${response.status}`;
    throw new Error(errorMsg);
  }
  const data = await response.json();
  return data.conversationSid;
}

export async function deleteConversation({ caseId, accessionId, patientWhatsAppNumber } = {}) {
  const url = `${TWILIO_API_BASE}${endpoints.conversation}`;
  const body = JSON.stringify({
    caseId: caseId || undefined,
    accessionId: accessionId || undefined,
    patientWhatsAppNumber: patientWhatsAppNumber || undefined,
  });
  const response = await fetchWithRetry(url, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body,
  });
  if (!response.ok) {
    if (response.status === 404) return { deleted: false };
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Delete failed: ${response.status}`);
  }
  const data = await response.json();
  return data;
}

export async function getAvailableTemplates({ search, language } = {}) {
  const params = new URLSearchParams();
  if (search) params.set("search", search);
  const url = `${TWILIO_API_BASE}${endpoints.listContentTemplates}?${params.toString()}`;
  const response = await fetchWithRetry(url, { 
    method: "GET",
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Get templates failed: ${response.status}`);
  }
  const data = await response.json();
  return data;
}

export async function getInviteTemplatePreview({ contentSid, language = "en", signal } = {}) {
  const params = new URLSearchParams();
  if (contentSid) params.set("contentSid", contentSid);
  if (language) params.set("language", language);
  const url = `${TWILIO_API_BASE}${endpoints.inviteTemplatePreview}?${params.toString()}`;
  const response = await fetchWithRetry(url, { 
    method: "GET",
    signal, // Pass abort signal to cancel request
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Get template preview failed: ${response.status}`);
  }
  const data = await response.json();
  return data;
}

export async function sendInviteTemplate({
  caseId,
  accessionId,
  patientWhatsAppNumber,
  contentSid,
  contentVariables,
  language = "en",
} = {}) {
  const url = `${TWILIO_API_BASE}${endpoints.sendInvite}`;
  const response = await fetchWithRetry(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      caseId: caseId || undefined,
      accessionId: accessionId || undefined,
      patientWhatsAppNumber: patientWhatsAppNumber || undefined,
      contentSid: contentSid || undefined,
      contentVariables: contentVariables || {},
      language: language || "en",
    }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Send invite failed: ${response.status}`);
  }
  const data = await response.json();
  return data;
}

/**
 * Send free-form WhatsApp message via Messages API
 * Requires 24-hour window to be open (user must have responded to template)
 * @param {{ caseId, accessionId, patientWhatsAppNumber, body }} opts
 */
export async function sendWhatsAppMessage({ caseId, accessionId, patientWhatsAppNumber, body } = {}) {
  const url = `${TWILIO_API_BASE}${endpoints.sendWhatsAppMessage}`;
  const response = await fetchWithRetry(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      caseId: caseId || undefined,
      accessionId: accessionId || undefined,
      patientWhatsAppNumber: patientWhatsAppNumber || undefined,
      body: (body || "").trim() || undefined,
    }),
  });
  
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    const error = new Error(err.error || `Send message failed: ${response.status}`);
    error.code = err.code;
    error.requiresTemplate = err.requiresTemplate;
    throw error;
  }
  
  const data = await response.json();
  return data;
}
